import {
    GraphQLTableAdapter,
    IcsDataTable,
    TablePadding,
    type TableApi,
} from '@ics-portal/ics-data-table';
import {
    forwardRef,
    useImperativeHandle,
    useMemo,
    useRef,
    type ReactNode,
} from 'react';
import apolloClient from '@api/client';
import { GET_COMPONENT_CREATE_REQUESTS_PAGED } from '@api/queries/component-requests-paged';
import { type GetComponentCreateRequestsPagedQuery } from '@gql/graphql';
import { RELOAD_GRIDS, useEventListener } from '@hooks/events';
import {
    REQUEST_COLUMNS,
    type RequestRow,
} from '@pages/notification-components/components/RequestsCard/RequestsTable/columns';
import { useRequestActions } from '@pages/notification-components/components/RequestsCard/RequestsTable/use-request-actions';

interface RequestsTableProps {
    selectedChange: (requests: RequestRow[]) => void;
}

export const RequestsTable = forwardRef<
    TableApi<RequestRow>,
    RequestsTableProps
>(function RequestsTable({ selectedChange }, ref): ReactNode {
    const actions = useRequestActions();
    const tableRef = useRef<TableApi<RequestRow>>(null);

    useImperativeHandle(ref, () => tableRef.current!);

    useEventListener(RELOAD_GRIDS, () => {
        tableRef.current?.refresh();
    });

    const adapter = useMemo(
        () =>
            new GraphQLTableAdapter<
                GetComponentCreateRequestsPagedQuery,
                RequestRow
            >({
                client: apolloClient,
                query: GET_COMPONENT_CREATE_REQUESTS_PAGED,
                extractConnection: (d) =>
                    d.componentCreateRequestsView ?? {
                        totalCount: 0,
                        edges: [],
                    },
                searchFieldPaths: [
                    'componentName',
                    'componentVersion',
                    'componentVendorName',
                ],
            }),
        [],
    );

    return (
        <IcsDataTable<RequestRow>
            ref={tableRef}
            id="notification-component-requests"
            description="List of all requests"
            adapter={adapter}
            columns={REQUEST_COLUMNS}
            actions={actions}
            selection="multi"
            padding={TablePadding.NONE}
            onSelectionChange={selectedChange}
            emptyState={{
                title: 'No requests',
                description: 'There are no component create requests yet.',
                noResultsTitle: 'No requests match your search.',
            }}
        />
    );
});
